<template>
    <div class="action-management">
        <div class="action-header">
            <h3 class="header-title">이행 세부항목 관리</h3>
            <div class="header-controls">
                <select v-model="filters.orgTargetIdx" class="filter-select" @change="loadActions">
                    <option :value="null">전체 부서</option>
                    <option
                        v-for="orgTarget in orgTargetList"
                        :key="orgTarget.orgTargetIdx"
                        :value="orgTarget.orgTargetIdx"
                    >
                        {{ orgTarget.organization?.orgName || `조직 #${orgTarget.orgIdx}` }}
                    </option>
                </select>
                <select v-model="filters.operCategoryIdx" class="filter-select" @change="loadActions">
                    <option :value="null">전체 카테고리</option>
                    <option
                        v-for="category in categoryList"
                        :key="category.operCategoryIdx"
                        :value="category.operCategoryIdx"
                    >
                        {{ category.categoryName }}
                    </option>
                </select>
                <select v-model="sortBy" class="filter-select">
                    <option value="regDate">등록일순</option>
                    <option value="targetEmissions">목표순</option>
                    <option value="reductRate">감축률순</option>
                    <option value="categoryName">카테고리순</option>
                </select>
                <button class="btn-add" @click="openAddModal"><i class="fa fa-plus"></i> 추가</button>
            </div>
        </div>

        <div v-if="loading" class="loading">
            <div class="spinner"></div>
            <span>로딩 중...</span>
        </div>
        <div v-else-if="actionList.length === 0" class="empty">
            등록된 항목이 없습니다.
            <button class="btn-link" @click="openAddModal">추가하기</button>
        </div>
        <div v-else class="table-container">
            <table class="action-table">
                <thead>
                    <tr>
                        <th>카테고리</th>
                        <th>부서</th>
                        <th>연도</th>
                        <th>목표</th>
                        <th>실제</th>
                        <th>기준</th>
                        <th>감축률</th>
                        <th>달성률</th>
                        <th>작업자</th>
                        <th>비고</th>
                        <th>관리</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in sortedActionList" :key="item.itemIdx">
                        <td class="cell-category">
                            {{ item.operCategory?.categoryName || `#${item.operCategoryIdx}` }}
                        </td>
                        <td class="cell-department">
                            {{ item.orgTarget?.organization?.orgName || "-" }}
                        </td>
                        <td class="cell-year">
                            {{ item.orgTarget?.pathway?.pathwayYear || "-" }}
                        </td>
                        <td class="cell-number">
                            {{ formatNumber(item.targetEmissions) }}
                        </td>
                        <td class="cell-number">
                            {{ item.actualEmissions ? formatNumber(item.actualEmissions) : "-" }}
                        </td>
                        <td class="cell-number">
                            {{ item.baseEmissions ? formatNumber(item.baseEmissions) : "-" }}
                        </td>
                        <td class="cell-number">
                            {{ item.reductRate != null ? formatNumber(item.reductRate) + "%" : "-" }}
                        </td>
                        <td class="cell-number">
                            {{
                                item.actualEmissions && item.targetEmissions
                                    ? formatNumber(calculateAchievement(item)) + "%"
                                    : "-"
                            }}
                        </td>
                        <td class="cell-text">
                            {{ item.worker || "-" }}
                        </td>
                        <td class="cell-remarks" :title="item.remarks">
                            {{
                                item.remarks
                                    ? item.remarks.length > 20
                                        ? item.remarks.substring(0, 20) + "..."
                                        : item.remarks
                                    : "-"
                            }}
                        </td>
                        <td class="cell-actions">
                            <IconButton icon="fas fa-edit" title="수정" @click="openEditModal(item)" />
                            <IconButton icon="fas fa-trash" variant="delete" title="삭제" @click="deleteAction(item)" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <b-modal v-model="showModal" :title="isEditMode ? '수정' : '추가'" size="lg" @hidden="resetForm">
            <div class="modal-form">
                <div class="form-row">
                    <label>부서별 이행계획 <span class="required">*</span></label>
                    <select v-model.number="form.orgTargetIdx" class="form-control" :disabled="isEditMode">
                        <option :value="null">선택</option>
                        <option
                            v-for="orgTarget in orgTargetList"
                            :key="orgTarget.orgTargetIdx"
                            :value="orgTarget.orgTargetIdx"
                        >
                            {{ orgTarget.organization?.orgName || `조직 #${orgTarget.orgIdx}` }}
                            ({{ orgTarget.pathway?.pathwayYear || "-" }}년)
                        </option>
                    </select>
                </div>
                <div class="form-row">
                    <label>카테고리 <span class="required">*</span></label>
                    <select v-model.number="form.operCategoryIdx" class="form-control">
                        <option :value="null">선택</option>
                        <option
                            v-for="category in categoryList"
                            :key="category.operCategoryIdx"
                            :value="category.operCategoryIdx"
                        >
                            {{ category.categoryName }}
                        </option>
                    </select>
                </div>
                <div class="form-row form-row-2">
                    <div>
                        <label>목표 배출량 ({{ emissionUnit }}) <span class="required">*</span></label>
                        <input
                            v-model.number="form.targetEmissions"
                            type="number"
                            class="form-control"
                            min="0"
                            step="0.001"
                        />
                    </div>
                    <div>
                        <label>기준 배출량 ({{ emissionUnit }})</label>
                        <input
                            v-model.number="form.baseEmissions"
                            type="number"
                            class="form-control"
                            min="0"
                            step="0.001"
                        />
                    </div>
                </div>
                <div class="form-row form-row-2">
                    <div>
                        <label>실제 배출량 ({{ emissionUnit }})</label>
                        <input
                            v-model.number="form.actualEmissions"
                            type="number"
                            class="form-control"
                            min="0"
                            step="0.001"
                        />
                    </div>
                    <div>
                        <label>감축률 (%)</label>
                        <input :value="calculatedReductionRate" type="text" class="form-control" disabled />
                    </div>
                </div>
                <div class="form-row">
                    <label>비고</label>
                    <textarea v-model="form.remarks" class="form-control" rows="2"></textarea>
                </div>
            </div>
            <template #modal-footer>
                <button class="btn btn-secondary" @click="showModal = false">취소</button>
                <button class="btn btn-primary" @click="saveAction" :disabled="saving || !isFormValid">
                    {{ saving ? "저장 중..." : "저장" }}
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import * as targetService from "../services/targetService";
import { TARGET_STORAGE_UNIT } from "../targetUnits";
import IconButton from "../../common/IconButton.vue";

export default {
    components: { IconButton },
    name: "ActionManagement",
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            orgTargetList: [],
            categoryList: [],
            actionList: [],
            loading: false,
            showModal: false,
            isEditMode: false,
            saving: false,
            sortBy: "regDate",
            filters: {
                orgTargetIdx: null,
                operCategoryIdx: null,
            },
            form: {
                itemIdx: null,
                orgTargetIdx: null,
                operCategoryIdx: null,
                targetEmissions: null,
                actualEmissions: null,
                baseEmissions: null,
                remarks: "",
            },
        };
    },
    computed: {
        emissionUnit() {
            return TARGET_STORAGE_UNIT;
        },
        sortedActionList() {
            const list = [...this.actionList];
            switch (this.sortBy) {
                case "targetEmissions":
                    return list.sort((a, b) => (b.targetEmissions || 0) - (a.targetEmissions || 0));
                case "reductRate":
                    return list.sort((a, b) => (b.reductRate || 0) - (a.reductRate || 0));
                case "categoryName":
                    return list.sort((a, b) => {
                        const nameA = a.operCategory?.categoryName || "";
                        const nameB = b.operCategory?.categoryName || "";
                        return nameA.localeCompare(nameB, "ko");
                    });
                case "regDate":
                default:
                    return list.sort((a, b) => new Date(b.regDate) - new Date(a.regDate));
            }
        },
        calculatedReductionRate() {
            if (!this.form.baseEmissions || !this.form.targetEmissions) return "-";
            if (this.form.baseEmissions === 0) return "0.00";
            const rate = ((this.form.baseEmissions - this.form.targetEmissions) / this.form.baseEmissions) * 100;
            return rate.toFixed(2);
        },
        isFormValid() {
            return !!(
                this.form.orgTargetIdx &&
                this.form.operCategoryIdx &&
                this.form.targetEmissions != null &&
                this.form.targetEmissions >= 0
            );
        },
    },
    watch: {
        isActive: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.initialize();
                }
            },
        },
    },
    methods: {
        async initialize() {
            await Promise.all([this.loadOrgTargets(), this.loadCategories()]);
            await this.loadActions();
        },
        async loadOrgTargets() {
            try {
                this.orgTargetList = await targetService.fetchDepartmentalPlans(null);
            } catch (error) {
                console.error("부서별 이행계획 목록 조회 실패:", error);
                this.$bvToast.toast("부서별 이행계획 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        async loadCategories() {
            try {
                this.categoryList = await targetService.fetchCategories();
            } catch (error) {
                console.error("카테고리 목록 조회 실패:", error);
                this.$bvToast.toast("카테고리 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        async loadActions() {
            this.loading = true;
            try {
                const params = {};
                if (this.filters.orgTargetIdx) params.orgTargetIdx = this.filters.orgTargetIdx;
                if (this.filters.operCategoryIdx) params.operCategoryIdx = this.filters.operCategoryIdx;

                this.actionList = await targetService.fetchImplementationActions(params);
            } catch (error) {
                console.error("이행 세부항목 목록 조회 실패:", error);
                this.$bvToast.toast("이행 세부항목 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loading = false;
            }
        },
        openAddModal() {
            this.isEditMode = false;
            this.resetForm();
            this.showModal = true;
        },
        openEditModal(item) {
            this.isEditMode = true;
            this.form = { ...item };
            this.showModal = true;
        },
        async saveAction() {
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            try {
                const payload = { ...this.form };

                if (this.isEditMode) {
                    await targetService.updateImplementationAction(this.form.itemIdx, payload);
                    this.$bvToast.toast("이행 세부항목이 수정되었습니다.", {
                        variant: "success",
                        title: "저장 완료",
                    });
                } else {
                    await targetService.createImplementationAction(payload);
                    this.$bvToast.toast("이행 세부항목이 추가되었습니다.", {
                        variant: "success",
                        title: "저장 완료",
                    });
                }

                this.showModal = false;
                await this.loadActions();
            } catch (error) {
                console.error("이행 세부항목 저장 실패:", error);
                this.$bvToast.toast(error.message || "이행 세부항목 저장에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.saving = false;
            }
        },
        async deleteAction(item) {
            const categoryName = item.operCategory?.categoryName || `카테고리 #${item.operCategoryIdx}`;
            const confirmed = await this.$bvModal.msgBoxConfirm(`"${categoryName}" 항목을 삭제하시겠습니까?`, {
                title: "삭제 확인",
                okVariant: "danger",
                okTitle: "삭제",
                cancelTitle: "취소",
            });

            if (!confirmed) return;

            try {
                await targetService.deleteImplementationAction(item.itemIdx);
                this.$bvToast.toast("이행 세부항목이 삭제되었습니다.", {
                    variant: "success",
                    title: "삭제 완료",
                });
                await this.loadActions();
            } catch (error) {
                console.error("이행 세부항목 삭제 실패:", error);
                this.$bvToast.toast(error.message || "이행 세부항목 삭제에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        validateForm() {
            if (!this.form.orgTargetIdx) {
                this.$bvToast.toast("부서별 이행계획을 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.operCategoryIdx) {
                this.$bvToast.toast("카테고리를 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.targetEmissions) {
                this.$bvToast.toast("목표 배출량을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            return true;
        },
        resetForm() {
            this.form = {
                itemIdx: null,
                orgTargetIdx: null,
                operCategoryIdx: null,
                targetEmissions: null,
                actualEmissions: null,
                baseEmissions: null,
                remarks: "",
            };
        },
        formatNumber(num) {
            if (num == null || num === "") return "0";
            return Number(num).toLocaleString("ko-KR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
        },
        calculateAchievement(item) {
            if (!item.actualEmissions || !item.targetEmissions) return 0;
            return (item.actualEmissions / item.targetEmissions) * 100;
        },
    },
};
</script>

<style scoped>
.action-management {
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #fff;
}

.action-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
}

.header-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0;
}

.header-controls {
    display: flex;
    gap: 8px;
    align-items: center;
}

.filter-select {
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 13px;
    background: white;
    cursor: pointer;
}

.filter-select:focus {
    outline: none;
    border-color: #52b788;
}

.btn-add {
    padding: 6px 12px;
    background: #52b788;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.btn-add:hover {
    background: #40916c;
}

.btn-add i {
    margin-right: 4px;
}

.loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 20px;
    justify-content: center;
    color: #6b7280;
    font-size: 13px;
}

.empty {
    padding: 20px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
}

.btn-link {
    margin-left: 8px;
    color: #52b788;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    font-size: 13px;
}

.btn-link:hover {
    color: #40916c;
}

.table-container {
    flex: 1;
    overflow: auto;
    border: 1px solid #e5e7eb;
}

.action-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.action-table thead {
    background: #f9fafb;
    position: sticky;
    top: 0;
    z-index: 10;
}

.action-table th {
    padding: 8px 6px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    background: #f9fafb;
}

.action-table tbody tr:hover {
    background: #f9fafb;
}

.action-table td {
    padding: 6px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 12px;
    color: #4b5563;
    vertical-align: middle;
}

.cell-category {
    font-weight: 500;
}

.cell-department {
    font-weight: 500;
}

.cell-year {
    text-align: center;
    color: #6b7280;
}

.cell-number {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
}

.cell-text {
    color: #6b7280;
}

.cell-remarks {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #9ca3af;
    font-size: 11px;
}

.cell-actions {
    white-space: nowrap;
}

.spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #f3f4f6;
    border-top-color: #52b788;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.modal-form {
    padding: 8px 0;
}

.form-row {
    margin-bottom: 12px;
}

.form-row:last-child {
    margin-bottom: 0;
}

.form-row label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
}

.form-row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.form-control {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 3px;
    font-size: 13px;
    background: white;
}

.form-control:focus {
    outline: none;
    border-color: #52b788;
}

.form-control:disabled {
    background: #f9fafb;
    color: #6b7280;
}

.required {
    color: #ef4444;
}

.btn {
    padding: 6px 14px;
    border: none;
    border-radius: 3px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.btn-secondary {
    background: #f3f4f6;
    color: #4b5563;
}

.btn-secondary:hover {
    background: #e5e7eb;
}

.btn-primary {
    background: #52b788;
    color: white;
}

.btn-primary:hover {
    background: #40916c;
}

.btn-primary:disabled {
    background: #d1d5db;
    cursor: not-allowed;
}
</style>
